// m-ASS.16 (FRONT-J3 M7): extraído de DeliverySignatureDialog.tsx sem mudança de lógica.
import { supabase } from '@/integrations/supabase/client';
import type { CollectionTermItem } from '@/components/Documents/Templates/CollectionTermDocument';

/**
 * Itens de uma coleta de locação: collection_items é o snapshot de INTENÇÃO
 * do agendamento; vazio = coleta genérica/total → fallback nos itens reais
 * do contrato (movement_items via rentals.movement_id — modelo canônico).
 * Compartilhada pelo termo de coleta e pela UI do Driver (§I.93 Ato 3).
 */
export async function fetchRentalCollectionItems(
  rentalId: string,
  companyId: string | null | undefined,
  rawCollectionItems?: unknown,
): Promise<CollectionTermItem[]> {
  let items: CollectionTermItem[] = Array.isArray(rawCollectionItems)
    ? (rawCollectionItems as any[]).map((it: any) => ({
        name: it?.name ?? '—',
        quantity: Number(it?.quantity ?? 1),
        type: it?.type ?? null,
        serial_number: it?.serial_number ?? null,
      }))
    : [];

  if (items.length === 0 && companyId) {
    const { data: rentalMovement } = await supabase
      .from('rentals')
      .select('movement_id')
      .eq('id', rentalId)
      .eq('company_id', companyId)
      .maybeSingle();

    if (rentalMovement?.movement_id) {
      const { data: movementItems } = await supabase
        .from('movement_items')
        .select('quantity, notes, cylinder_type_id, equipment_id, product:products!cylinder_type_id(name), equipment:equipment!equipment_id(name, serial_number)')
        .eq('movement_id', rentalMovement.movement_id)
        .eq('company_id', companyId);

      items = ((movementItems ?? []) as any[]).map((mi) => {
        if (mi.equipment_id) {
          return {
            name: mi.equipment?.name ?? mi.notes ?? 'Equipamento',
            quantity: Number(mi.quantity ?? 1),
            type: 'equipment' as const,
            serial_number: mi.equipment?.serial_number ?? null,
          };
        }
        return {
          name: mi.product?.name ?? mi.notes ?? 'Cilindro',
          quantity: Number(mi.quantity ?? 1),
          type: 'cylinder' as const,
          serial_number: null,
        };
      });
    }
  }

  return items;
}

// ─── L-LOC.9b FRONT-R — coleta parcial (D-LOC.31/36/37) ─────────────────────

/** Linha do contrato tal como está hoje (movement_items), com o id que a RPC espera. */
export interface RentalContractItem {
  /** movement_items.id — é o `movement_item_id` de p_items_collected */
  movement_item_id: string;
  name: string;
  quantity: number;
  type: 'cylinder' | 'equipment';
  serial_number: string | null;
  /** item por unidade (série): o banco só aceita coleta inteira (D-LOC.37) */
  is_unit: boolean;
}

/** Quantidade marcada por item (0 = não coletado). */
export type CollectionSelection = Record<string, number>;

export interface CollectionSelectionSummary {
  selectedQty: number;
  totalQty: number;
  isEmpty: boolean;
  isPartial: boolean;
}

/**
 * Itens reais do contrato, com id de linha e marca de unidade.
 * Fonte: movement_items via rentals.movement_id (modelo canônico §I.92).
 */
export async function fetchRentalContractItems(
  rentalId: string,
  companyId: string | null | undefined,
): Promise<RentalContractItem[]> {
  if (!companyId) return [];

  const { data: rentalMovement } = await supabase
    .from('rentals')
    .select('movement_id')
    .eq('id', rentalId)
    .eq('company_id', companyId)
    .maybeSingle();

  if (!rentalMovement?.movement_id) return [];

  const { data: movementItems } = await supabase
    .from('movement_items')
    .select(
      'id, quantity, notes, cylinder_type_id, cylinder_id, equipment_id, ' +
      'product:products!cylinder_type_id(name), ' +
      'equipment:equipment!equipment_id(name, serial_number), ' +
      'cylinder:cylinders!cylinder_id(serial_number, product:products!cylinders_product_id_fkey(name))',
    )
    .eq('movement_id', rentalMovement.movement_id)
    .eq('company_id', companyId);

  return ((movementItems ?? []) as any[]).map((mi) => {
    if (mi.equipment_id) {
      return {
        movement_item_id: mi.id,
        name: mi.equipment?.name ?? mi.notes ?? 'Equipamento',
        quantity: Number(mi.quantity ?? 1),
        type: 'equipment' as const,
        serial_number: mi.equipment?.serial_number ?? null,
        is_unit: true,
      };
    }
    if (mi.cylinder_id) {
      // m-LOC.11: cilindro por unidade guarda só cylinder_id; o nome vem do produto da unidade
      return {
        movement_item_id: mi.id,
        name: mi.product?.name ?? mi.cylinder?.product?.name ?? mi.notes ?? 'Cilindro',
        quantity: Number(mi.quantity ?? 1),
        type: 'cylinder' as const,
        serial_number: mi.cylinder?.serial_number ?? null,
        is_unit: true,
      };
    }
    return {
      movement_item_id: mi.id,
      name: mi.product?.name ?? mi.notes ?? 'Cilindro',
      quantity: Number(mi.quantity ?? 1),
      type: 'cylinder' as const,
      serial_number: null,
      is_unit: false,
    };
  });
}

/**
 * Converte os itens de um contrato (items[] de get_rentals_filtered, ou o
 * subtipo mínimo do balcão) para o formato da lista de coleta.
 */
export function contractItemsFromRental(
  items: Array<{
    id: string;
    type: 'cylinder' | 'equipment';
    name: string;
    serial_number?: string | null;
    quantity: number;
    cylinder_id?: string | null;
    equipment_id?: string | null;
  }>,
): RentalContractItem[] {
  return items.map((it) => ({
    movement_item_id: it.id,
    name: it.name,
    quantity: Number(it.quantity ?? 1),
    type: it.type,
    serial_number: it.serial_number ?? null,
    is_unit: !!it.cylinder_id || !!it.equipment_id,
  }));
}

/** Seleção inicial: tudo marcado, quantidade cheia (coleta total é o caso comum). */
export function fullSelection(items: RentalContractItem[]): CollectionSelection {
  const sel: CollectionSelection = {};
  for (const it of items) sel[it.movement_item_id] = it.quantity;
  return sel;
}

export function summarizeSelection(
  items: RentalContractItem[],
  selection: CollectionSelection,
): CollectionSelectionSummary {
  let selectedQty = 0;
  let totalQty = 0;
  for (const it of items) {
    totalQty += it.quantity;
    const q = selection[it.movement_item_id] ?? 0;
    selectedQty += Math.max(0, Math.min(q, it.quantity));
  }
  return {
    selectedQty,
    totalQty,
    isEmpty: selectedQty === 0,
    isPartial: totalQty > 0 && selectedQty < totalQty,
  };
}

/** Payload de p_items_collected: só o que tem quantidade > 0. */
export function buildItemsCollectedPayload(
  items: RentalContractItem[],
  selection: CollectionSelection,
): Array<{ movement_item_id: string; quantity: number }> {
  return items
    .map((it) => ({
      movement_item_id: it.movement_item_id,
      quantity: Math.max(0, Math.min(selection[it.movement_item_id] ?? 0, it.quantity)),
    }))
    .filter((it) => it.quantity > 0);
}

/** Itens do termo a partir do que o front marcou (fallback quando a RPC não devolve `collected`). */
export function termItemsFromSelection(
  items: RentalContractItem[],
  selection: CollectionSelection,
): CollectionTermItem[] {
  return items
    .filter((it) => (selection[it.movement_item_id] ?? 0) > 0)
    .map((it) => ({
      name: it.name,
      quantity: Math.min(selection[it.movement_item_id] ?? 0, it.quantity),
      type: it.type,
      serial_number: it.serial_number,
    }));
}

/**
 * Itens do termo a partir do `collected` devolvido pela RPC (o que saiu DE FATO).
 * Depois da confirmação, os itens do contrato já são só o que ficou — nunca
 * reler o contrato para montar o termo (§I.147 herança item 1).
 */
export function termItemsFromCollected(collected: unknown): CollectionTermItem[] {
  if (!Array.isArray(collected)) return [];
  return (collected as any[]).map((c) => ({
    name: c?.name ?? '—',
    quantity: Number(c?.quantity ?? 1),
    type: c?.type ?? null,
    serial_number: c?.serial_number ?? null,
  }));
}
